import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type SortingState } from '@tanstack/react-table'
import { DataTable } from '@/components/data-table'
import { createSponsorSubEventColumns } from './SponsorSubEventColumns'
import { useQuery } from '@tanstack/react-query'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { fetchSponsorEvents } from '@/api/sponsorApi'
import { transformSponsorSubEventListData } from '@/constants/sponsor'
import { PATHROUTES } from '@/constants/path'
import type {
  DisplaySponsorSubEvent,
  SponsorSubEventListType,
} from '@/schemas/sponsor.schems'
import SponsorshipSummaryDialog from './SponsorshipSummaryDialog'

export default function SponsorSubEventTable() {
  const navigate = useNavigate()
  const [sorting, setSorting] = useState<SortingState>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [pagination, setPagination] = useState({
    pageIndex: currentPage - 1,
    pageSize: pageSize,
  })
  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)

  const {
    data: sponsorSubEventData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      SPONSOR_QUERY_KEYS.allEvents,
      SPONSOR_QUERY_KEYS.subEvents,
      currentPage,
      pageSize,
      sorting,
    ],
    queryFn: () =>
      fetchSponsorEvents({
        limit: pageSize,
        page: currentPage,
        sort: sorting,
        isSubEvent: true,
      }),
  })

  const rawSubEvents: SponsorSubEventListType[] =
    sponsorSubEventData?.data?.result || []
  const displaySubEvents: DisplaySponsorSubEvent[] = rawSubEvents.map(
    transformSponsorSubEventListData
  )
  const totalItems = sponsorSubEventData?.data?.totalCount || 0
  const totalPages =
    sponsorSubEventData?.data?.totalPages ||
    Math.ceil(totalItems / pageSize) ||
    0

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setCurrentPage(1)
  }

  const handleProceedToPayment = (eventId: string) => {
    setSelectedEventId(eventId)
    setIsSummaryDialogOpen(true)
  }

  const handleEditPackages = (eventId: string) => {
    navigate(`${PATHROUTES.DASHBOARD.SELECT_PACKAGE_WITH_ID(eventId)}`)
  }

  const sponsorSubEventColumns = createSponsorSubEventColumns(
    handleProceedToPayment,
    handleEditPackages
  )

  return (
    <>
      <DataTable
        title="Admin Assigned Sub Event"
        data={displaySubEvents}
        columns={sponsorSubEventColumns}
        sorting={sorting}
        setSorting={setSorting}
        pagination={pagination}
        setPagination={setPagination}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        showFilter={false}
        showExport={false}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        error={error?.message || null}
        isLoading={isLoading}
        emptyState={{
          image: '/auth/empty-eventlist.svg',
          title: 'No sub events found',
          subtitle:
            'There are no sub events available at the moment. Please check back later.',
        }}
      />

      <SponsorshipSummaryDialog
        open={isSummaryDialogOpen}
        onOpenChange={setIsSummaryDialogOpen}
        sponsorShipId={selectedEventId as string}
      />
    </>
  )
}
